import { plateKindLabels } from "@car/api/catalog-config";
import { Badge } from "@car/ui/components/badge";
import { Button } from "@car/ui/components/button";
import { Card, CardContent } from "@car/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@car/ui/components/dialog";
import { useAppForm } from "@car/ui/components/form";
import { Separator } from "@car/ui/components/separator";
import { Skeleton } from "@car/ui/components/skeleton";
import { optionalText, requiredChoice } from "@car/ui/lib/form-rules";
import {
  File01Icon,
  Image01Icon,
  Invoice01Icon,
  Wrench01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import {
  DocumentSigners,
  PageHeader,
  PanelEmpty,
  PanelQueryError,
  RatingStars,
} from "@/components/panel-bits";
import { faDate, faKm, faNumber, faPercent, faPlainNumber, faToman } from "@/lib/format";
import { serviceTypeLabels, type Proforma, type ServiceRecord } from "@/lib/panel";
import { TargetPayments } from "@/components/target-payments";
import { PayDialog } from "@/routes/pay/-pay-dialog";
import { client, orpc } from "@/utils/orpc";

import { SEP } from "@car/ui/lib/utils";

export const Route = createFileRoute("/panel/history")({
  head: () => ({ meta: [{ title: "سوابق و فاکتورها | پنل مشتریان تابان خودرو" }] }),
  component: HistoryPage,
});

function HistoryPage() {
  const query = useQuery(orpc.panel.history.list.queryOptions());

  return (
    <div>
      <PageHeader
        title="سوابق و فاکتورها"
        description="همه خدمات انجام‌شده، هزینه‌ها و نظرسنجی رضایت شما."
      />

      {query.isPending ? (
        <div className="grid gap-3">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-28 rounded-xl" />
          ))}
        </div>
      ) : query.isError ? (
        <PanelQueryError title="سوابق و فاکتورها بارگیری نشد" query={query} />
      ) : query.data.services.length === 0 && query.data.proformas.length === 0 ? (
        <PanelEmpty
          icon={Wrench01Icon}
          title="هنوز سرویسی ثبت نشده"
          description="پس از اولین مراجعه به شعب خدمات، سوابق اینجا نمایش داده می‌شود."
        />
      ) : (
        <div className="grid gap-3">
          {query.data.proformas.map((proforma) => (
            <ProformaCard key={proforma.id} proforma={proforma} />
          ))}
          {query.data.services.map((record) => {
            return (
              <Card key={record.id}>
                <CardContent className="grid gap-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="grid gap-0.5">
                      <span className="font-bold">
                        {record.title}
                        {record.vehicleName ? (
                          <span className="font-normal text-muted-foreground">
                            {SEP}
                            {record.vehicleName}
                          </span>
                        ) : null}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {[
                          faDate(record.dateIso),
                          record.branchName,
                          record.odometerKm > 0 ? `کارکرد ${faKm(record.odometerKm)}` : "",
                          serviceTypeLabels[record.serviceType],
                        ]
                          .filter(Boolean)
                          .join(SEP)}
                      </span>
                    </div>
                    {record.voided ? (
                      <div className="flex items-center gap-2">
                        <s className="font-black text-muted-foreground">
                          {faToman(record.voided.amount)}
                        </s>
                        <Badge variant="outline">باطل شده</Badge>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-black">{faToman(record.total)}</span>
                        <Badge
                          className={
                            record.paid
                              ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                              : "bg-amber-500/15 text-amber-800 dark:text-amber-400"
                          }
                        >
                          {record.paid ? "پرداخت شده" : "در انتظار پرداخت"}
                        </Badge>
                      </div>
                    )}
                  </div>

                  <TargetPayments
                    purpose="service_invoice"
                    targetId={record.id}
                    title="پرداخت‌های این فاکتور"
                  />

                  <Separator />

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <InvoiceDialog record={record} />
                      {record.photoCount > 0 ? <PhotosDialog record={record} /> : null}
                      {!record.paid && !record.voided && record.total > 0 ? (
                        <PayDialog
                          purpose="service_invoice"
                          targetId={record.id}
                          title={invoiceLabel(record)}
                        >
                          پرداخت
                        </PayDialog>
                      ) : null}
                    </div>
                    {record.surveyed && record.rating ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        نظر شما: <RatingStars value={record.rating} size="size-4" />
                      </div>
                    ) : record.surveyable ? (
                      <SurveyDialog
                        serviceId={record.id}
                        title={record.title}
                        points={query.data.surveyPoints}
                      />
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** پیش‌فاکتور فروش خودرو که کارشناس فروش صادر کرده است؛ تا پایان اعتبار قابل مراجعه است */
function ProformaCard({ proforma }: { proforma: Proforma }) {
  return (
    <Card>
      <CardContent className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="grid gap-0.5">
            <span className="font-bold">
              پیش‌فاکتور {faPlainNumber(proforma.number)}
              <span className="font-normal text-muted-foreground">
                {SEP}
                {proforma.vehicleTitle}
              </span>
            </span>
            <span className="text-sm text-muted-foreground">
              {[
                `مدل ${faPlainNumber(proforma.modelYear)}`,
                proforma.color,
                plateKindLabels[proforma.plateKind],
                `صدور ${faDate(proforma.createdIso)}`,
              ]
                .filter(Boolean)
                .join(SEP)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-black">{faToman(proforma.total)}</span>
            <Badge
              className={
                proforma.expired
                  ? "bg-muted text-muted-foreground"
                  : "bg-amber-500/15 text-amber-800 dark:text-amber-400"
              }
            >
              {proforma.expired ? "اعتبار تمام شده" : `معتبر تا ${faDate(proforma.validUntilIso)}`}
            </Badge>
          </div>
        </div>
        <Separator />
        <dl className="grid gap-1.5 text-sm">
          <ProformaRow label="قیمت خودرو" value={faToman(proforma.unitPrice)} />
          {proforma.discount > 0 ? (
            <ProformaRow label="تخفیف" value={faToman(proforma.discount)} />
          ) : null}
          {proforma.vatAmount > 0 ? (
            <ProformaRow
              label={`ارزش افزوده (${faPercent(proforma.vatPercent)})`}
              value={faToman(proforma.vatAmount)}
            />
          ) : null}
          {proforma.months > 0 ? (
            <>
              <ProformaRow label="طرح" value={proforma.planTitle} />
              <ProformaRow label="پیش‌پرداخت" value={faToman(proforma.downPayment)} />
              <ProformaRow
                label={`قسط ماهانه (${faPlainNumber(proforma.months)} ماهه)`}
                value={faToman(proforma.monthly)}
              />
            </>
          ) : (
            <ProformaRow label="روش پرداخت" value="نقدی" />
          )}
        </dl>
        {proforma.note ? <p className="text-sm text-muted-foreground">{proforma.note}</p> : null}
        <DocumentSigners signers={proforma.signers} />
        {proforma.hasFile ? <ProformaFileButtons proformaId={proforma.id} /> : null}
      </CardContent>
    </Card>
  );
}

/** فایل پیوست پیش‌فاکتور؛ پیوندها پنج دقیقه اعتبار دارند، پس با هر کلیک تازه گرفته می‌شوند */
function ProformaFileButtons({ proformaId }: { proformaId: string }) {
  const [busy, setBusy] = useState(false);
  const open = async (download: boolean) => {
    setBusy(true);
    try {
      const link = await client.panel.history.proformaFile({ id: proformaId });
      if (!link?.url || !link.downloadUrl) throw new Error("missing");
      // the download link carries «attachment», so the page stays where it is
      if (download) window.location.assign(link.downloadUrl);
      else window.open(link.url, "_blank", "noopener");
    } catch {
      toast.error("باز کردن فایل ممکن نشد؛ دوباره تلاش کنید.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="outline" size="sm" disabled={busy} onClick={() => open(false)}>
        <HugeiconsIcon icon={File01Icon} strokeWidth={1.8} />
        مشاهده فایل پیش‌فاکتور
      </Button>
      <Button variant="ghost" size="sm" disabled={busy} onClick={() => open(true)}>
        دانلود
      </Button>
    </div>
  );
}

function ProformaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-bold">{value}</dd>
    </div>
  );
}

/** «فاکتور F-1404-2117» یا، برای سابقه دستی بدون شماره، عنوان سرویس */
const invoiceLabel = (record: ServiceRecord) =>
  record.invoiceNumber ? `فاکتور ${record.invoiceNumber}` : record.title;

function InvoiceDialog({ record }: { record: ServiceRecord }) {
  return (
    <Dialog>
      <DialogTrigger render={<Button variant="outline" size="sm" />}>
        <HugeiconsIcon icon={Invoice01Icon} strokeWidth={1.8} />
        مشاهده {invoiceLabel(record)}
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <DialogHeader>
          <DialogTitle>{invoiceLabel(record)}</DialogTitle>
          <DialogDescription>
            {[record.title, record.vehicleName, faDate(record.dateIso)].filter(Boolean).join(SEP)}
          </DialogDescription>
        </DialogHeader>
        <ul className="grid gap-2 text-sm">
          {record.lines.map((line, index) => (
            <li key={index} className="flex items-center justify-between gap-3">
              <span className="text-muted-foreground">{line.title}</span>
              <span className="font-bold">{faToman(line.amount)}</span>
            </li>
          ))}
        </ul>
        <Separator />
        <div className="flex items-center justify-between font-black">
          <span>جمع کل</span>
          {record.voided ? (
            <span className="flex items-center gap-2">
              <s className="text-muted-foreground">{faToman(record.voided.amount)}</s>
              <Badge variant="outline">باطل شده</Badge>
            </span>
          ) : (
            <span>{faToman(record.total)}</span>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * تصاویر وضعیت خودرو هنگام پذیرش. فایل‌ها خصوصی‌اند: لینک‌ها فقط با باز شدن پنجره و برای
 * صاحب همین سرویس امضا می‌شوند و پنج دقیقه اعتبار دارند (پیش از آن، ۴ دقیقه، تازه می‌شوند).
 */
function PhotosDialog({ record }: { record: ServiceRecord }) {
  const [open, setOpen] = useState(false);
  const query = useQuery({
    ...orpc.panel.history.photos.queryOptions({ input: { serviceId: record.id } }),
    enabled: open,
    staleTime: 4 * 60_000,
  });
  const photos = query.data?.photos ?? [];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="outline" size="sm" />}>
        <HugeiconsIcon icon={Image01Icon} strokeWidth={1.8} />
        تصاویر پذیرش ({faNumber(record.photoCount)})
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl" className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>تصاویر وضعیت خودرو هنگام پذیرش</DialogTitle>
          <DialogDescription>
            {[record.title, record.vehicleName, faDate(record.dateIso)].filter(Boolean).join(SEP)}؛
            برای دیدن اندازه کامل، روی تصویر بزنید.
          </DialogDescription>
        </DialogHeader>
        {query.isPending ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {Array.from({ length: Math.min(record.photoCount, 6) }, (_, index) => (
              <Skeleton key={index} className="aspect-[4/3] rounded-lg" />
            ))}
          </div>
        ) : query.isError ? (
          <PanelQueryError title="تصاویر بارگیری نشد" query={query} />
        ) : photos.length === 0 ? (
          <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            تصویری برای این سرویس در دسترس نیست.
          </p>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {photos.map((photo, index) => (
              <li key={photo.id}>
                <a
                  href={photo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-[4/3] overflow-hidden rounded-lg border bg-muted/40 outline-none focus-visible:ring-3 focus-visible:ring-ring/40"
                >
                  <img
                    src={photo.url}
                    alt={`تصویر ${faNumber(index + 1)} وضعیت خودرو`}
                    loading="lazy"
                    className="size-full object-cover"
                  />
                </a>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

// امتیاز ستاره‌ها هم مثل بقیه فیلدها رشته نگه داشته می‌شود؛ خالی یعنی هنوز ستاره‌ای انتخاب نشده
const surveySchema = z.object({
  rating: requiredChoice("امتیاز"),
  comment: optionalText("نظر", 500),
});

const surveyInitial = { rating: "", comment: "" };

function SurveyDialog({
  serviceId,
  title,
  points,
}: {
  serviceId: string;
  title: string;
  /** the configured survey reward; 0 = no points promised */
  points: number;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const mutation = useMutation(
    orpc.panel.history.submitSurvey.mutationOptions({
      onSuccess: (result) => {
        if (result.ok) {
          toast.success(
            result.points > 0
              ? `نظر شما ثبت شد؛ ${faNumber(result.points)} امتیاز باشگاه گرفتید`
              : "نظر شما ثبت شد؛ از همراهی‌تان سپاسگزاریم",
            // a low score opened a follow-up for the service manager
            result.followUp
              ? {
                  description: "از تجربه‌تان متأسفیم؛ مدیر خدمات تا یک روز کاری با شما تماس می‌گیرد.",
                }
              : undefined,
          );
          queryClient.invalidateQueries();
          setOpen(false);
        } else {
          toast.error(result.reason);
        }
      },
    }),
  );

  const form = useAppForm({
    defaultValues: surveyInitial,
    validators: { onBlur: surveySchema },
    onSubmit: async ({ value }) => {
      await mutation
        .mutateAsync({ serviceId, rating: Number(value.rating), comment: value.comment.trim() })
        .catch(() => undefined);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // هر بار باز شدن، نظرسنجی از صفر شروع می‌شود
        if (next) form.reset(surveyInitial);
        setOpen(next);
      }}
    >
      <DialogTrigger render={<Button size="sm" />}>
        {points > 0 ? "ثبت نظر و دریافت امتیاز" : "ثبت نظر"}
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <form.AppForm>
          <form.Form className="grid gap-4">
            <DialogHeader>
              <DialogTitle>رضایت شما از «{title}»</DialogTitle>
              <DialogDescription>
                امتیاز بدهید و اگر نکته‌ای دارید بنویسید
                {points > 0 ? `؛ ${faNumber(points)} امتیاز باشگاه هدیه می‌گیرید.` : "."}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <form.AppField name="rating">
                {(field) => (
                  <field.FormField label="امتیاز شما">
                    {({ controlProps }) => (
                      <div
                        id={controlProps.id}
                        aria-invalid={controlProps["aria-invalid"]}
                        aria-describedby={controlProps["aria-describedby"]}
                        className="flex justify-center"
                      >
                        <RatingStars
                          value={Number(field.state.value) || 0}
                          onChange={(star) => {
                            field.handleChange(String(star));
                            field.handleBlur();
                          }}
                          size="size-8"
                        />
                      </div>
                    )}
                  </field.FormField>
                )}
              </form.AppField>
              <form.AppField name="comment">
                {(field) => (
                  <field.TextareaField
                    label="نظر شما (اختیاری)"
                    placeholder="اگر نکته‌ای دارید بنویسید"
                    maxLength={500}
                    rows={3}
                  />
                )}
              </form.AppField>
            </div>
            <DialogFooter>
              <form.SubmitButton pending={mutation.isPending}>ثبت نظر</form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}
